import React, { useEffect, useState } from "react";
import { Modal, Popconfirm, Spin } from "antd";
import axios from "axios";
import { path } from "../../path";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

const UrlShortenerModal = ({ open, toggle }) => {
  const { token } = useSelector((state) => state.AuthReducer);

  const [originalUrl, setOriginalUrl] = useState("");
  const [identifier, setIdentifier] = useState("ad");
  const [shortUrl, setShortUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [links, setLinks] = useState([]);
  const [fetching, setFetching] = useState(false);

  const config = {
    headers: {
      "Access-Control-Allow-Origin": `${path}`,
      Authorization: `Bearer ${token}`,
    },
  };

  useEffect(() => {
    if (open) {
      fetchLinks();
    }
  }, [open]);

  const fetchLinks = async () => {
    setFetching(true);
    try {
      const res = await axios.get(`${path}/shorten/links`, config);
      setLinks(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load links");
    } finally {
      setFetching(false);
    }
  };

  const handleShorten = async () => {
    if (!originalUrl.trim()) {
      return toast.error("Please enter a valid URL!");
    }
    setLoading(true);
    try {
      const res = await axios.post(`${path}/shorten`, {
        originalUrl,
        identifier,
      }, config);
      setShortUrl(res.data.shortUrl);
      toast.success("Short URL created!");
      fetchLinks(); // refresh list
      setOriginalUrl("");
    } catch (err) {
      console.error(err);
      toast.error("Failed to shorten URL");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Copied to clipboard!");
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${path}/shorten/${id}`, config);
      toast.success("Link deleted!");
      setLinks((prev) => prev.filter((link) => link._id !== id));
    } catch (err) {
      toast.error("Failed to delete link");
    }
  };

  return (
    <Modal
      open={open}
      onCancel={toggle}
      centered
      footer={null}
      maskClosable={false}
      title="🔗 Manage Short URLs"
      width={600}
      destroyOnClose
    >
      <div className="flex flex-col gap-3">
        {/* Input Section */}
        <div className="flex flex-col gap-3 border-b pb-4">
          <input
            className="border border-gray-300 rounded-md p-2 w-full"
            type="text"
            placeholder="Enter original URL"
            value={originalUrl}
            onChange={(e) => setOriginalUrl(e.target.value)}
          />

          <select
            className="border border-gray-300 rounded-md p-2 w-full"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
          >
            <option value="ad">Advertisement</option>
            <option value="comp">Contest</option>
            <option value="other">Other</option>
          </select>

          <button
            className={`${
              loading ? "bg-blue-300 cursor-not-allowed" : "bg-blue-500"
            } text-white px-4 py-2 rounded-md font-medium transition-colors`}
            onClick={handleShorten}
            disabled={loading}
          >
            {loading ? "Generating..." : "Shorten URL"}
          </button>

          {shortUrl && (
            <div className="mt-3">
              <p className="text-gray-700 mb-1">New Short URL:</p>
              <div className="flex items-center justify-between bg-gray-50 border rounded-md px-3 py-2">
                <a
                  className="text-blue-600 underline truncate"
                  href={shortUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {shortUrl}
                </a>
                <button
                  onClick={() => handleCopy(shortUrl)}
                  className="ml-2 text-sm font-medium text-gray-600 hover:text-blue-500"
                >
                  Copy
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Links List */}
        <div className="mt-3 max-h-[300px] overflow-y-auto border-t pt-3">
          <h3 className="text-lg font-medium mb-2">Your Links</h3>

          {fetching ? (
            <div className="flex justify-center items-center py-6">
              <Spin />
            </div>
          ) : links.length === 0 ? (
            <p className="text-gray-500 text-sm">No links found.</p>
          ) : (
            links.map((link) => (
              <div
                key={link._id}
                className="flex items-center justify-between border-b py-2"
              >
                <div className="flex flex-col w-[75%]">
                  <a
                    href={link.shortUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-600 font-medium truncate"
                  >
                    {link.shortUrl}
                  </a>
                  <p className="text-sm text-gray-500 truncate">
                    {link.originalUrl}
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-600">
                    {link.clicks} clicks
                  </span>

                  <button
                    onClick={() => handleCopy(link.shortUrl)}
                    className="text-gray-500 hover:text-blue-500 text-sm"
                  >
                    Copy
                  </button>

                  <Popconfirm
                    title="Delete link?"
                    okText="Yes"
                    cancelText="No"
                    onConfirm={() => handleDelete(link._id)}
                  >
                    <button className="text-red-500 hover:text-red-600 text-sm">
                      Delete
                    </button>
                  </Popconfirm>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
};

export default UrlShortenerModal;